import { FC, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../../common/basic/QcsButton';
import { SlideListItem } from './SlideListItem';
import AddIcon from '@mui/icons-material/Add';
import { QcsBox } from '../../common/basic/QcsBox';
import { QcsLoadingButton } from '../../common/basic/QcsLoadingButton';
import { setErrorStateSnacks } from '../../../utils/error';
import { useAppSnackbar } from '../../../hooks/useAppSnackbar';
import { SaveError } from '../../common/SaveError';
import { ErrorStateType } from '../../../models/common';
import { SlideDto } from '@api/models/training/SlideDto';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AddSlideButtonWrapper, DroppableContainer, SlidesScrollArea } from './SlideList.styles';

interface Props {
  slides: SlideDto[];
  selectedId?: string;
  isDragDisabled?: boolean;
  onItemClick: (id: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  addSlide: () => void;
  onCloseEditor: () => void;
}

export const SlideList: FC<Props> = ({ slides, selectedId, isDragDisabled, onItemClick, onDragEnd, addSlide, onCloseEditor }) => {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useAppSnackbar();
  const [isClosingEditor, setIsClosingEditor] = useState(false);
  const [saveError, setSaveError] = useState<ErrorStateType>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleCloseEditor = () => {
    setIsClosingEditor(true);
    try {
      onCloseEditor();
    } catch (err) {
      setErrorStateSnacks(err, setSaveError, enqueueErrorSnackbar, 'common.saveError');
    }
    setIsClosingEditor(false);
  };

  const slideIds = slides.map((slide) => slide.id!);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={slideIds} strategy={verticalListSortingStrategy}>
        <DroppableContainer>
            <QcsBox
              sx={{
                display: 'flex',
                mb: '1rem',
                justifyContent: 'center',
              }}
            >
              <QcsLoadingButton variant="contained" onClick={handleCloseEditor} disabled={isClosingEditor} loading={isClosingEditor}>
                <ArrowBackIcon />
                {t('common.back')}
              </QcsLoadingButton>
            </QcsBox>
            <QcsBox
              sx={{
                display: 'flex',
                mb: '1rem',
                justifyContent: 'center',
              }}
            >
              <QcsButton variant="contained" startIcon={<AddIcon />} onClick={addSlide} disabled={isDragDisabled}>
                {t('training.addSlide')}
              </QcsButton>
            </QcsBox>
          <SlidesScrollArea>
            <SaveError error={saveError} />
            {slides.map((slide) => (
              <SlideListItem
                key={slide.id}
                id={slide.id!}
                title={slide.title}
                onClick={onItemClick}
                selected={selectedId === slide.id}
                isDragDisabled={isDragDisabled}
              />
            ))}
          </SlidesScrollArea>

            <AddSlideButtonWrapper>
              <QcsButton variant="contained" startIcon={<AddIcon />} onClick={addSlide} disabled={isDragDisabled}>
                {t('training.addSlide')}
              </QcsButton>
            </AddSlideButtonWrapper>
        </DroppableContainer>
      </SortableContext>
    </DndContext>
  );
};
